import { z } from 'zod'

export const MOCK_SCENARIOS = [
  'empty',
  'populated',
  'authenticated-employee',
  'authenticated-customer',
  'error',
] as const

const RuntimeConfigSchema = z.object({
  API_BASE_URL: z.string().default('/api'),
  // Единый SSO хаба (00-architecture § 3): origin employee-backend (id.stit.tech) —
  // продление и выход сотрудника (/auth/refresh|logout). Дефолт '' (same-origin,
  // MSW/dev). В проде — https://id.stit.tech (compose → window.__APP_CONFIG__).
  ID_BASE_URL: z.string().default(''),
  // Контур заказчика (00-architecture § 3): origin company-backend — продление
  // (/auth/customer/refresh) и выход заказчика идут туда напрямую, мимо deal.
  // Дефолт '' (same-origin, MSW/dev). В проде — https://api.hub.stit.tech;
  // апекс продукта обязан быть в COMPANY_CORS_ORIGINS, иначе preflight режется
  // и продление вырождается в разлогин.
  HUB_API_BASE_URL: z.string().default(''),
  // Единая точка входа экосистемы (22-unified-login): страница хаба, куда уходят
  // обе аудитории, когда сессии нет. Своей формы входа у deal нет — дефолт
  // указывает на прод-страницу, падать локально некуда.
  HUB_LOGIN_URL: z.string().default('https://hub.stit.tech/login'),
  // APP_TZ — зона организации (IANA), одна на всех: в ней UI считает границы
  // суток («сегодня», «просрочено», период отчёта). Момент — «когда начнётся» —
  // показывается и вводится в зоне устройства и конфигом не приходит. Тот же
  // контракт, что у бэка (tzkit).
  APP_TZ: z.string().min(1).default('Europe/Moscow'),
  API_MODE: z.enum(['mock', 'real']).default('mock'),
  MOCK_SCENARIO: z.enum(MOCK_SCENARIOS).default('authenticated-employee'),
  MOCK_DEVTOOLS: z.boolean().default(false),
  SENTRY_DSN: z.string().default(''),
  SENTRY_ENVIRONMENT: z.string().default('production'),
  RELEASE: z.string().default('unknown'),
})

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>

declare global {
  interface Window {
    __APP_CONFIG__?: unknown
  }
}

function readConfig(): RuntimeConfig {
  const env = import.meta.env as Record<string, string | undefined>
  const fromEnv = {
    APP_TZ: env.VITE_APP_TZ,
    ID_BASE_URL: env.VITE_ID_BASE_URL,
    HUB_API_BASE_URL: env.VITE_HUB_API_BASE_URL,
    HUB_LOGIN_URL: env.VITE_HUB_LOGIN_URL,
    API_MODE: env.VITE_API_MODE,
    MOCK_SCENARIO: env.VITE_MOCK_SCENARIO,
    MOCK_DEVTOOLS: env.VITE_MOCK_DEVTOOLS === '1',
  }
  const fromWindow =
    typeof window !== 'undefined' && window.__APP_CONFIG__
      ? (window.__APP_CONFIG__ as Record<string, unknown>)
      : {}
  return RuntimeConfigSchema.parse({ ...fromEnv, ...fromWindow })
}

export const runtimeConfig = readConfig()
